import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import ReplyItem from './ReplyItem';
import { FaHeart, FaRegHeart, FaReply, FaEllipsisH } from 'react-icons/fa';
import { IoMdArrowDropdown, IoMdArrowDropup } from 'react-icons/io';

export default function CommentItem({ comment, contentId, contentType, onReply }) {
    const auth = getAuth();
    const [isLiked, setIsLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(comment.likes || 0);
    const [showReplies, setShowReplies] = useState(false);
    const [replies, setReplies] = useState([]);
    const [isLoadingReplies, setIsLoadingReplies] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    // Check if current user liked this comment
    useEffect(() => {
        if (auth.currentUser) {
            setIsLiked(comment.likedBy?.includes(auth.currentUser.uid) || false);
        }
    }, [auth.currentUser, comment.likedBy]);

    // Handle like/unlike
    const handleLike = async () => {
        if (!auth.currentUser) {
            toast.error("Please sign in to like comments");
            return;
        }

        try {
            const collectionPath = contentType === 'movie' 
                ? `movies/${contentId}/comments` 
                : `status/${contentId}/comments`;
            const commentRef = doc(db, collectionPath, comment.id);

            if (isLiked) {
                await updateDoc(commentRef, {
                    likes: likeCount - 1,
                    likedBy: arrayRemove(auth.currentUser.uid)
                });
                setLikeCount(likeCount - 1);
                setIsLiked(false);
            } else {
                await updateDoc(commentRef, {
                    likes: likeCount + 1,
                    likedBy: arrayUnion(auth.currentUser.uid)
                });
                setLikeCount(likeCount + 1);
                setIsLiked(true);
            }
        } catch (error) {
            console.error("Error updating like status:", error);
            toast.error("Failed to update like");
        }
    };

    // Load replies when toggled
    useEffect(() => {
        if (!showReplies || !comment.replies || comment.replies.length === 0) return;

        const loadReplies = async () => {
            setIsLoadingReplies(true);
            try {
                const collectionPath = contentType === 'movie' 
                    ? `movies/${contentId}/comments` 
                    : `status/${contentId}/comments`;
                
                const repliesData = await Promise.all(
                    comment.replies.map(async (replyId) => {
                        const replyRef = doc(db, collectionPath, replyId);
                        const replySnap = await getDoc(replyRef);
                        if (replySnap.exists()) {
                            return { id: replySnap.id, ...replySnap.data() };
                        }
                        return null;
                    })
                );

                setReplies(repliesData.filter(reply => reply !== null));
            } catch (error) {
                console.error("Error loading replies:", error);
                toast.error("Failed to load replies");
            } finally {
                setIsLoadingReplies(false);
            }
        };

        loadReplies();
    }, [showReplies, comment.replies, contentId, contentType]);

    // Toggle text expansion for long comments
    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    // Format timestamp
    const formatTime = (timestamp) => {
        if (!timestamp?.toDate) return 'Just now';
        const now = new Date();
        const commentDate = timestamp.toDate();
        const diffInSeconds = Math.floor((now - commentDate) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
        return `${Math.floor(diffInSeconds / 86400)}d`;
    };

    return (
        <div className="comment-item">
            <style jsx>{`
                .comment-item {
                    position: relative;
                    padding-right: 16px;
                }
                
                .comment-text {
                    display: -webkit-box;
                    -webkit-line-clamp: ${isExpanded ? 'unset' : '3'};
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    word-break: break-word;
                }
                
                .read-more-btn {
                    color: #a1a1aa;
                    font-size: 14px;
                    cursor: pointer;
                    display: inline-block;
                    margin-top: 4px;
                }
                
                .comment-actions {
                    display: flex;
                    align-items: center;
                    margin-top: 8px;
                    gap: 16px;
                }
                
                .like-action {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    cursor: pointer;
                }
                
                .reply-action {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    cursor: pointer;
                }
                
                .replies-toggle {
                    display: flex;
                    align-items: center;
                    color: #a1a1aa;
                    font-size: 14px;
                    cursor: pointer;
                    margin-top: 8px;
                }
                
                .replies-container {
                    margin-left: 32px;
                    margin-top: 12px;
                    border-left: 2px solid #3f3f46;
                    padding-left: 12px;
                }
            `}</style>

            <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                    <img 
                        src={comment.userPhoto || '/default-avatar.jpg'} 
                        alt={comment.userName}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                            e.target.src = '/default-avatar.jpg';
                        }}
                    />
                </div>
                <div className="flex-1">
                    <div className="flex items-center space-x-2">
                        <span className="font-semibold text-sm">{comment.userName}</span>
                        <span className="text-gray-400 text-xs">{formatTime(comment.timestamp)}</span>
                    </div>
                    <div className="mt-1">
                        <p className={`text-sm text-white comment-text`}>
                            {comment.text}
                        </p>
                        {comment.text.length > 150 && (
                            <span 
                                onClick={toggleExpand}
                                className="read-more-btn"
                            >
                                {isExpanded ? 'Show less' : 'Read more'}
                            </span>
                        )}
                    </div>
                    
                    <div className="comment-actions">
                        <div className="like-action" onClick={handleLike}>
                            {isLiked ? (
                                <FaHeart className="text-red-500" />
                            ) : (
                                <FaRegHeart className="text-gray-400 hover:text-red-500" />
                            )}
                            <span className="text-xs">{likeCount}</span>
                        </div>
                        <div 
                            className="reply-action"
                            onClick={() => onReply(comment)}
                        >
                            <FaReply className="text-gray-400 hover:text-gray-200" />
                            <span className="text-xs">Reply</span>
                        </div>
                    </div>
                    
                    {comment.replyCount > 0 && (
                        <div 
                            className="replies-toggle"
                            onClick={() => setShowReplies(!showReplies)}
                        >
                            {showReplies ? (
                                <>
                                    <IoMdArrowDropup />
                                    <span>Hide {comment.replyCount} replies</span>
                                </>
                            ) : (
                                <>
                                    <IoMdArrowDropdown />
                                    <span>View {comment.replyCount} replies</span>
                                </>
                            )}
                        </div>
                    )}
                    
                    {showReplies && (
                        <div className="replies-container">
                            {isLoadingReplies ? (
                                <div className="flex justify-center py-2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-pink-500"></div>
                                </div>
                            ) : (
                                replies.map((reply) => (
                                    <ReplyItem 
                                        key={reply.id} 
                                        reply={reply} 
                                        contentId={contentId} 
                                        contentType={contentType} 
                                        onReply={onReply}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}